import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { FileStack } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { DateCell } from "@/components/DateCell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface BlanketPo {
  id: string;
  blanketNumber: string;
  status: string;
  commitmentType: string;
  pricingModel: string;
  committedValue: string | null;
  consumedValue: string | null;
  validFrom: string;
  validTo: string;
  createdAt: string;
  vendor?: { id: string; name: string };
}

interface BlanketPosResponse {
  blanketPos: BlanketPo[];
  total: number;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING_APPROVAL", label: "Pending Approval" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export default function BlanketPosPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading } = useQuery<BlanketPosResponse>({
    queryKey: ["/api/blanket-pos", { search: searchValue, page: currentPage, limit: pageSize, status: statusFilter }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
      });
      if (searchValue) params.set("search", searchValue);
      if (statusFilter !== "all") params.set("status", statusFilter);
      return api.get<BlanketPosResponse>(`/blanket-pos?${params}`, sessionId);
    },
  });

  const columns = [
    {
      key: "createdAt",
      header: "Entry",
      sortable: true,
      cell: (row: BlanketPo) => <DateCell value={row.createdAt} className="text-muted-foreground" />,
    },
    {
      key: "blanketNumber",
      header: "Blanket #",
      sortable: true,
      cell: (row: BlanketPo) => (
        <div className="font-medium" data-testid={`text-blanket-number-${row.id}`}>{row.blanketNumber}</div>
      ),
    },
    {
      key: "vendor",
      header: "Vendor",
      cell: (row: BlanketPo) => <span>{row.vendor?.name || "-"}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (row: BlanketPo) => <StatusBadge status={row.status} type="procurement" />,
    },
    {
      key: "commitmentType",
      header: "Type",
      cell: (row: BlanketPo) => (
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{row.commitmentType}</span>
      ),
    },
    {
      key: "pricingModel",
      header: "Pricing",
      cell: (row: BlanketPo) => (
        <span className="text-xs font-medium">{row.pricingModel?.replace(/_/g, " ")}</span>
      ),
    },
    {
      key: "committedValue",
      header: "Committed",
      className: "text-right",
      cell: (row: BlanketPo) => (
        <span className="font-medium">
          {row.committedValue ? `$${Number(row.committedValue).toLocaleString()}` : "-"}
        </span>
      ),
    },
    {
      key: "consumedValue",
      header: "Consumed",
      className: "text-right",
      cell: (row: BlanketPo) => (
        <span className="text-muted-foreground">${Number(row.consumedValue || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "validFrom",
      header: "Valid From",
      cell: (row: BlanketPo) => <DateCell value={row.validFrom} className="text-muted-foreground" />,
    },
    {
      key: "validTo",
      header: "Valid To",
      cell: (row: BlanketPo) => <DateCell value={row.validTo} className="text-muted-foreground" />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Blanket Purchase Orders"
        description="Standing procurement authorizations with commitment tracking"
        actions={
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search blanket POs..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-64"
            />
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.blanketPos || []}
        totalCount={data?.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/blanket-pos/${row.id}`)}
        emptyIcon={<FileStack className="h-12 w-12 text-muted-foreground/50" />}
        emptyMessage="No blanket purchase orders"
      />
    </div>
  );
}
